import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Activity, Award } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";

export default function Certificate() {
  const { courseId } = useParams();
  const { t } = useLang();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/certificates/${courseId}`)
      .then((r) => setCert(r.data))
      .catch((err) => setError(formatApiError(err)));
  }, [courseId]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="certificate-error">
        <p className="text-[#DC2626] font-semibold">{error}</p>
        <Link to="/dashboard" className="btn-outline-light mt-8">{t("cert.back")}</Link>
      </div>
    );
  }
  if (!cert) return <p className="max-w-7xl mx-auto px-6 py-16 text-[#64748B]" data-testid="certificate-loading">{t("common.loading")}</p>;

  const issued = cert.issued_at ? new Date(cert.issued_at).toLocaleDateString("el-GR") : "-";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10" data-testid="certificate-page">
      <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <Link data-testid="certificate-back-link" to="/dashboard" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#475569] hover:text-[#1D4ED8] transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> {t("cert.back")}
        </Link>
        <button data-testid="certificate-print-button" onClick={() => window.print()} className="btn-blue !px-6 !py-2.5 text-sm">
          <Printer className="w-4 h-4" /> {t("cert.print")}
        </button>
      </div>

      <div
        className="mt-8 bg-[#EEF4FF] text-[#0B1B3A] rounded-[28px] border-4 border-[#1D4ED8] p-8 sm:p-14 relative overflow-hidden print:rounded-none print:border-2 print:mt-0"
        data-testid="certificate-card"
      >
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#1D4ED8]/20" />
        <div className="absolute -left-20 -bottom-20 w-64 h-64 rounded-full bg-[#1D4ED8]/10" />

        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-[#1D4ED8] flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" strokeWidth={2.5} />
            </span>
            <span className="font-heading font-black text-lg">SportScience Academy</span>
          </div>

          <p className="mt-10 text-[11px] font-bold uppercase tracking-[0.3em] text-[#64748B]">{t("cert.heading")}</p>
          <p className="mt-6 text-sm text-[#475569]">{t("cert.awarded")}</p>
          <h1 className="font-heading font-black text-3xl sm:text-5xl mt-2" data-testid="certificate-student-name">
            {cert.student_name}
          </h1>

          <p className="mt-8 text-sm text-[#475569]">{t("cert.for")}</p>
          <h2 className="font-heading font-black text-2xl sm:text-3xl mt-2">
            <span className="bg-[#1D4ED8] text-white px-3 rounded-[12px] inline-block" data-testid="certificate-course-title">{cert.course_title}</span>
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("cert.months")}</p>
              <p className="font-heading font-black text-2xl mt-1">{cert.duration_months} {t("program.months")}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("cert.avg")}</p>
              <p className="font-heading font-black text-2xl mt-1" data-testid="certificate-avg">{cert.avg_score}%</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("cert.date")}</p>
              <p className="font-heading font-black text-2xl mt-1">{issued}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("cert.code")}</p>
              <p className="font-heading font-black text-lg mt-1 break-all" data-testid="certificate-code">{cert.code}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-8 mt-14 pt-8 border-t-2 border-[#E2E8F0]">
            <div>
              <p className="font-heading font-black text-xl italic">Mike Kouk</p>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B] mt-1">{t("cert.signature")}</p>
            </div>
            <div className="w-24 h-24 rounded-full bg-[#0B1B3A] border border-[#1D4ED8]/50 flex flex-col items-center justify-center text-center">
              <Award className="w-7 h-7 text-[#1D4ED8]" />
              <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#1D4ED8] mt-1">Certified</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
